import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { IconType } from "react-icons";
import {
  FiAlertTriangle, FiArrowRight, FiCheckCircle, FiClock, FiCpu, FiEdit2, FiHelpCircle, FiKey, FiLock,
  FiMinusCircle, FiMoreHorizontal, FiPlus, FiPower, FiRefreshCw, FiSearch, FiShare2, FiShield, FiSliders, FiTrash2,
  FiXCircle, FiZap, FiGlobe, FiGrid, FiCalendar,
} from "react-icons/fi";

import {
  createACSServer, createAlertRule, deleteACSServer, extendGraphShare, listACSServers, listAlertRules, listGraphShares,
  listONTs, revokeGraphShare, settingsSummary, syncACSServer, testACSServer, updateACSServer, updateAlertRule, updateOLT,
  type ACSServerForm, type GraphShare, type SettingsSummary,
} from "@/lib/api";
import { useAuth } from "@/lib/auth";
import type { ACSServer } from "@/lib/types";
import { bandsOf, rxRuleFor } from "@/lib/bands";
import { classifyRx, type SignalBands, type SignalLevel } from "@/lib/signal";
import { everyText } from "@/lib/integrations";
import {
  acsState, endpointOf, impact, policyError, previewSamples, searchSettings,
  type AcsState, type SearchEntry, type Section,
} from "@/lib/settings";
import { useIntegrations, useNow } from "@/components/MonitoringBanner";
import { durText } from "@/components/OntStatus";
import ShareDialog from "@/components/ShareDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtDate, fmtDateTime, fmtNum, useI18n, type Key } from "@/lib/i18n";
import { cn } from "@/lib/utils";

type T = (k: Key, p?: Record<string, string | number>) => string;
type Summary = SettingsSummary;

const SECTIONS: Section[] = ["overview", "general", "monitoring", "integrations", "access", "sharing", "advanced"];
const ICON: Record<Section, IconType> = {
  overview: FiGrid, general: FiGlobe, monitoring: FiSliders, integrations: FiZap, access: FiKey, sharing: FiShare2, advanced: FiCpu,
};
const ago = (iso: string, now: number, t: T) => t("ua.ago", { age: durText(now - Date.parse(iso)) });

// ---------- small parts ----------

type Tone = "good" | "warn" | "crit" | "muted" | "info";
const TONE: Record<Tone, { text: string; bg: string; dot: string }> = {
  good: { text: "text-signal-good", bg: "bg-signal-good/10", dot: "bg-signal-good" },
  warn: { text: "text-signal-warn", bg: "bg-amber-400/10", dot: "bg-signal-warn" },
  crit: { text: "text-signal-crit", bg: "bg-signal-crit/10", dot: "bg-signal-crit" },
  muted: { text: "text-slate-400", bg: "bg-slate-700/50", dot: "bg-slate-500" },
  info: { text: "text-signal", bg: "bg-signal/10", dot: "bg-signal" },
};

function Badge({ tone, icon: I, children }: { tone: Tone; icon?: IconType; children: ReactNode }) {
  return (
    <span className={cn("inline-flex items-center gap-1.5 whitespace-nowrap rounded-sm px-1.5 py-0.5 text-[10.5px] font-semibold tracking-[0.12em] uppercase", TONE[tone].text, TONE[tone].bg)}>
      {I && <I className="size-3" />} {children}
    </span>
  );
}

const Dot = ({ tone }: { tone: Tone }) => <span className={cn("inline-block size-1.5 shrink-0 rounded-full", TONE[tone].dot)} />;

function Panel({ id, title, right, children, className }: { id?: string; title?: string; right?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section id={id ? `set-${id}` : undefined} className={cn("scroll-mt-4 rounded-lg border border-rule bg-panel transition-shadow", className)}>
      {(title || right) && (
        <header className="flex min-h-11 flex-wrap items-center justify-between gap-2 border-b border-rule px-4 py-2">
          {title && <h2 className="eyebrow text-slate-300">{title}</h2>}
          {right}
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

/** Label/value pair; values are data, labels are quiet. */
const Field = ({ label, children, className }: { label: string; children: ReactNode; className?: string }) => (
  <div className={cn("min-w-0", className)}>
    <dt className="text-[11px] text-slate-500">{label}</dt>
    <dd className="data mt-0.5 truncate text-[13px] text-slate-100">{children}</dd>
  </div>
);

function ViewOnly({ what }: { what: Key }) {
  const { t } = useI18n();
  return (
    <p className="flex items-center gap-2 rounded-sm border border-rule bg-white/[0.02] px-3 py-1.5 text-[12px] text-slate-400">
      <FiLock className="size-3.5 shrink-0" /> {t("set.viewOnly", { what: t(what) })}
    </p>
  );
}

type MenuItem = { icon: IconType; label: string; onClick: () => void; danger?: boolean } | "sep";

function Menu({ label, items }: { label: string; items: MenuItem[] }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent | KeyboardEvent) => {
      if (e instanceof KeyboardEvent ? e.key === "Escape" : !ref.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", close);
    window.addEventListener("keydown", close);
    return () => { window.removeEventListener("mousedown", close); window.removeEventListener("keydown", close); };
  }, [open]);
  return (
    <div ref={ref} className="relative inline-block">
      <button type="button" aria-haspopup="menu" aria-expanded={open} aria-label={label} onClick={() => setOpen(!open)}
        className="grid size-7 place-items-center rounded-sm text-slate-400 hover:bg-white/5 hover:text-slate-100">
        <FiMoreHorizontal />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 z-30 mt-1 w-52 overflow-hidden rounded-md border border-rule bg-[#070b14] py-1 text-left shadow-2xl">
          {items.map((it, i) => it === "sep" ? <div key={i} className="my-1 border-t border-rule" /> : (
            <button key={i} type="button" role="menuitem" onClick={() => { setOpen(false); it.onClick(); }}
              className={cn("flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12.5px]",
                it.danger ? "text-rose-300 hover:bg-signal-crit/10" : "text-slate-200 hover:bg-white/[0.05]")}>
              <it.icon className="size-3.5 shrink-0" /> {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Confirmation for anything disruptive: says what happens, names the thing. */
function Confirm({ title, body, action, danger, pending, error, onConfirm, onClose, children }: {
  title: string; body: ReactNode; action: string; danger?: boolean; pending?: boolean; error?: unknown;
  onConfirm: () => void; onClose: () => void; children?: ReactNode;
}) {
  const { t } = useI18n();
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="text-slate-400">{body}</DialogDescription>
        </DialogHeader>
        {children}
        {!!error && <p className="text-sm text-signal-crit">{(error as Error).message}</p>}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant={danger ? "destructive" : "default"} disabled={pending} onClick={onConfirm}>{action}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- page ----------

export default function SettingsPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const admin = user?.role === "admin";
  const [sp, setSp] = useSearchParams();
  const section = (SECTIONS.includes(sp.get("s") as Section) ? sp.get("s") : "overview") as Section;
  const summary = useQuery({ queryKey: ["settings-summary"], queryFn: settingsSummary, refetchInterval: 60_000 });
  const rules = useQuery({ queryKey: ["alert-rules"], queryFn: listAlertRules });
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const go = (s: Section, anchor?: string) => {
    if (s !== section) {
      if (dirty && !confirm(t("set.leaveDirty"))) return;
      setDirty(false);
      const p = new URLSearchParams(sp);
      if (s === "overview") p.delete("s"); else p.set("s", s);
      setSp(p);
    }
    if (!anchor) return;
    // After the section has rendered: scroll to the setting and mark it briefly.
    setTimeout(() => {
      const el = document.getElementById(`set-${anchor}`);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.classList.add("ring-1", "ring-signal/60");
      setTimeout(() => el.classList.remove("ring-1", "ring-signal/60"), 1600);
    }, 60);
  };

  const s = summary.data;
  return (
    <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
      <aside className="space-y-3 lg:sticky lg:top-3 lg:w-64 lg:shrink-0">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-slate-100">{t("set.title")}</h1>
          <p className="mt-0.5 text-[12px] text-slate-500">{t("set.subtitle")}</p>
        </div>
        <SearchBox onPick={(e) => go(e.section, e.anchor)} />
        <nav className="flex flex-wrap gap-1 lg:flex-col" aria-label={t("set.title")}>
          {SECTIONS.map((x) => {
            const I = ICON[x];
            const st = s ? navStatus(x, s, rules.data?.rules, admin, t) : null;
            return (
              <button key={x} type="button" aria-current={section === x ? "page" : undefined} onClick={() => go(x)}
                className={cn("flex items-center gap-2.5 rounded-sm border-l-2 px-2.5 py-1.5 text-left text-[13px]",
                  section === x ? "border-signal bg-signal/10 text-slate-100" : "border-transparent text-slate-400 hover:bg-white/[0.03] hover:text-slate-100")}>
                <I className="size-3.5 shrink-0" />
                <span className="flex-1">{t(`set.nav.${x}` as Key)}</span>
                {st && <span className="hidden items-center gap-1.5 text-[11px] text-slate-500 lg:inline-flex"><Dot tone={st.tone} />{st.text}</span>}
              </button>
            );
          })}
        </nav>
        {user && (
          <div className="rounded-sm border border-rule px-3 py-2 text-[11.5px] text-slate-400">
            <p className="eyebrow mb-0.5">{t("set.yourAccess")}</p>
            <p>{t(`set.access.${user.role}` as Key)}</p>
          </div>
        )}
      </aside>

      <main className="min-w-0 max-w-[1480px] flex-1 space-y-3">
        <p className="eyebrow">
          {t("set.title")}{section !== "overview" && <> <span className="text-slate-600">/</span> <span className="text-slate-300">{t(`set.nav.${section}` as Key)}</span></>}
        </p>
        {summary.error && <p className="text-signal-crit">{(summary.error as Error).message}</p>}
        {summary.isLoading && <p className="text-slate-400">{t("common.loading")}</p>}
        {s && section === "overview" && <Overview s={s} go={go} />}
        {s && section === "general" && <General s={s} />}
        {s && section === "monitoring" && <Monitoring s={s} onDirty={setDirty} />}
        {s && section === "integrations" && <Integrations s={s} />}
        {s && section === "access" && <Access s={s} go={go} />}
        {section === "sharing" && <Sharing />}
        {s && section === "advanced" && <Advanced s={s} />}
      </main>
    </div>
  );
}

function navStatus(x: Section, s: Summary, rules: Parameters<typeof bandsOf>[0], admin: boolean, t: T): { tone: Tone; text: string } | null {
  switch (x) {
    case "monitoring": {
      const b = bandsOf(rules);
      return { tone: rxRuleFor(rules, "warn") && rxRuleFor(rules, "critical") ? "good" : "warn", text: `${b.warn} / ${b.crit}` };
    }
    case "integrations": {
      const c = configured(s);
      return { tone: c.n === c.total ? "good" : "warn", text: `${c.n}/${c.total}` };
    }
    case "access":
      return s.security.vault_configured ? { tone: "good", text: t("set.enc.short") } : { tone: "crit", text: t("set.enc.shortOff") };
    case "sharing":
      return admin ? { tone: s.sharing.active ? "info" : "muted", text: t("set.nActive", { n: s.sharing.active }) } : null;
    default:
      return null;
  }
}

/** Integrations configured on this deployment; health lives on the Integrations page. */
function configured(s: Summary) {
  const i = s.integrations;
  const items: [string, boolean][] = [
    ["ACS", i.acs_servers > 0], ["LibreNMS", i.librenms.configured], ["SMTP", i.smtp.configured],
    ["Telegram", i.telegram.configured], ["WHMCS", i.whmcs.configured],
  ];
  return { n: items.filter(([, ok]) => ok).length, total: items.length, missing: items.filter(([, ok]) => !ok).map(([n]) => n) };
}

// ---------- search ----------

function SearchBox({ onPick }: { onPick: (e: SearchEntry) => void }) {
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const index = useMemo<SearchEntry[]>(() => [
    { section: "general", anchor: "language", label: t("set.gen.language"), terms: "language bahasa locale" },
    { section: "general", anchor: "system", label: t("set.gen.system"), terms: "public url timezone invite link" },
    { section: "general", anchor: "permissions", label: t("set.gen.permissions"), terms: "role permission admin noc viewer access" },
    { section: "monitoring", anchor: "optical", label: t("set.opt.title"), terms: "optical rx threshold dbm signal warning critical power fibre" },
    { section: "monitoring", anchor: "alert-policies", label: t("set.mon.alertPolicies"), terms: "alert rules policy notifications" },
    { section: "monitoring", anchor: "freshness", label: t("set.mon.freshness"), terms: "polling interval stale freshness traffic utilisation utilization" },
    { section: "integrations", anchor: "acs", label: t("set.acs.title"), terms: "genieacs acs cpe tr069 nbi server" },
    { section: "integrations", anchor: "connections", label: "LibreNMS", terms: "topology librenms" },
    { section: "integrations", anchor: "connections", label: "SMTP", terms: "email mail smtp" },
    { section: "integrations", anchor: "connections", label: "Telegram", terms: "bot notifications chat telegram" },
    { section: "integrations", anchor: "connections", label: "WHMCS", terms: "billing subscribers whmcs" },
    { section: "access", anchor: "encryption", label: t("set.sec.title"), terms: "encryption master key aes security secret" },
    { section: "access", anchor: "olt-credentials", label: t("set.cred.title"), terms: "olt password username ssh telnet credential" },
    { section: "sharing", anchor: "shares", label: t("shares.title"), terms: "share link customer public expiry revoke graph" },
    { section: "advanced", anchor: "diagnostics", label: t("set.adv.title"), terms: "environment env variables system diagnostics OPS_MASTER_KEY" },
  ], [t]);
  const hits = searchSettings(index, q);
  const pick = (e: SearchEntry) => { setQ(""); onPick(e); };
  return (
    <div className="relative">
      <FiSearch className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-slate-500" />
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("set.search")} aria-label={t("set.search")}
        onKeyDown={(e) => { if (e.key === "Enter" && hits[0]) pick(hits[0]); if (e.key === "Escape") setQ(""); }}
        className="h-8 border-rule bg-panel pl-8 text-[12.5px]" />
      {q.trim() && (
        <div className="absolute inset-x-0 z-30 mt-1 overflow-hidden rounded-md border border-rule bg-[#070b14] py-1 shadow-2xl">
          {hits.length === 0 && <p className="px-3 py-1.5 text-[12px] text-slate-500">{t("set.noHits")}</p>}
          {hits.map((e) => (
            <button key={`${e.anchor}-${e.label}`} type="button" onClick={() => pick(e)}
              className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-[12.5px] text-slate-200 hover:bg-white/[0.05]">
              <span className="truncate">{e.label}</span>
              <span className="shrink-0 text-[11px] text-slate-500">{t(`set.nav.${e.section}` as Key)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- overview ----------

function Overview({ s, go }: { s: Summary; go: (s: Section, anchor?: string) => void }) {
  const { t, locale } = useI18n();
  const { user } = useAuth();
  const admin = user?.role === "admin";
  const now = useNow(30_000);
  const rules = useQuery({ queryKey: ["alert-rules"], queryFn: listAlertRules });
  const shares = useQuery({ queryKey: ["graph-shares"], queryFn: listGraphShares, enabled: admin });
  const b = bandsOf(rules.data?.rules);
  const policySet = !!rxRuleFor(rules.data?.rules, "warn") && !!rxRuleFor(rules.data?.rules, "critical");
  const c = configured(s);
  const sec = s.security;
  const expiring = (shares.data?.shares ?? []).filter((x) => x.status === "active" && x.expires_at && Date.parse(x.expires_at) - now < 7 * 86_400_000).length;

  const tiles: { sec: Section; tone: Tone; status: string; facts: [string, ReactNode][] }[] = [
    {
      sec: "monitoring", tone: policySet ? "good" : "warn", status: t(policySet ? "set.ov.policySet" : "set.ov.policyDefault"),
      facts: [[t("set.ov.rx"), `< ${b.warn} · < ${b.crit} dBm`], [t("set.ov.alertEval"), t("set.every", { d: everyText(s.monitoring.alert_eval_s * 1000) })]],
    },
    {
      sec: "integrations", tone: c.n === c.total ? "good" : "warn", status: t("set.ov.configured", { n: c.n, total: c.total }),
      facts: [[t("set.acs.title"), fmtNum(locale, s.integrations.acs_servers)], [t("set.ov.notConfigured"), c.missing.join(", ") || t("common.none")]],
    },
    {
      sec: "access", tone: sec.vault_configured ? "good" : "crit", status: t(sec.vault_configured ? "set.enc.on" : "set.enc.off"),
      facts: [[t("set.ov.oltCreds"), t("set.nOfM", { n: sec.olt_credentials, m: sec.olts })], [t("set.ov.reveal"), t("set.ov.writeOnly")]],
    },
    {
      sec: "sharing", tone: admin ? (s.sharing.active ? "info" : "muted") : "muted",
      status: admin ? t("set.nActiveLinks", { n: s.sharing.active }) : t("set.adminOnly"),
      facts: admin ? [[t("set.ov.expiring"), shares.data ? fmtNum(locale, expiring) : "—"], [t("set.ov.totalLinks"), fmtNum(locale, s.sharing.total)]] : [],
    },
    {
      sec: "general", tone: "muted", status: t("set.ov.serverWide"),
      facts: [[t("set.gen.tz"), s.general.olt_timezone || t("set.gen.tzUnset")], [t("set.gen.publicUrl"), s.general.public_base_url ? endpointOf(s.general.public_base_url) : t("set.notSet")]],
    },
    { sec: "advanced", tone: "muted", status: t("set.ov.sysInfo"), facts: [[t("set.ov.diag"), t("set.ov.envNames")]] },
  ];

  return (
    <div className="grid gap-3 2xl:grid-cols-[minmax(0,1fr)_400px]">
      <div className="grid content-start gap-3 md:grid-cols-2 xl:grid-cols-3">
        {tiles.map((x) => {
          const I = ICON[x.sec];
          return (
            <button key={x.sec} type="button" onClick={() => go(x.sec)}
              className="group flex min-h-40 flex-col rounded-lg border border-rule bg-panel p-4 text-left hover:border-signal/40">
              <span className="flex items-center gap-2 text-[13px] font-semibold text-slate-100"><I className="size-4 text-slate-400 group-hover:text-signal" /> {t(`set.nav.${x.sec}` as Key)}</span>
              <span className={cn("mt-2 flex items-center gap-2 text-[12.5px]", TONE[x.tone].text)}><Dot tone={x.tone} /> {x.status}</span>
              <dl className="mt-3 grid flex-1 content-start gap-1.5">
                {x.facts.map(([k, v]) => (
                  <div key={k} className="flex items-baseline justify-between gap-3 text-[12px]">
                    <dt className="shrink-0 text-slate-500">{k}</dt><dd className="data min-w-0 truncate text-slate-300">{v}</dd>
                  </div>
                ))}
              </dl>
              <span className="mt-3 inline-flex items-center gap-1 text-[12px] text-slate-400 group-hover:text-signal">{t("set.open")} <FiArrowRight className="size-3" /></span>
            </button>
          );
        })}
      </div>
      <Panel title={t("set.recent")}>
        <RecentChanges s={s} now={now} rulesById={Object.fromEntries((rules.data?.rules ?? []).map((r) => [r.id, r.name]))}
          sharesById={Object.fromEntries((shares.data?.shares ?? []).map((x) => [x.id, x.label]))} />
      </Panel>
    </div>
  );
}

function RecentChanges({ s, now, rulesById, sharesById }: {
  s: Summary; now: number; rulesById: Record<number, string>; sharesById: Record<number, string>;
}) {
  const { t, locale } = useI18n();
  if (!s.recent.length) return <p className="text-[12.5px] text-slate-500">{t("set.noRecent")}</p>;
  const target = (e: Summary["recent"][number]) => {
    const id = e.target_id;
    if (e.action.startsWith("alert_rule.") && id !== undefined && rulesById[id]) return rulesById[id];
    if (e.action.startsWith("graph_share.") && id !== undefined && sharesById[id]) return sharesById[id];
    return e.target || (id !== undefined ? `#${id}` : "");
  };
  return (
    <ol className="space-y-2.5">
      {s.recent.map((e, i) => (
        <li key={i} className="flex gap-2.5 text-[12.5px]">
          <span className={cn("mt-1.5 size-1.5 shrink-0 rounded-full", /delete|revoke/.test(e.action) ? "bg-signal-crit" : "bg-slate-500")} />
          <div className="min-w-0">
            <p className="text-slate-200">{t(`set.act.${e.action}` as Key)} <span className="font-medium text-slate-100">{target(e)}</span></p>
            <p className="text-[11.5px] text-slate-500" title={fmtDateTime(locale, e.at)}>
              {ago(e.at, now, t)}{e.actor_email && <> · {e.actor_email}</>}
            </p>
          </div>
        </li>
      ))}
    </ol>
  );
}

// ---------- general ----------

function General({ s }: { s: Summary }) {
  const { t, lang, setLang } = useI18n();
  const g = s.general;
  const roles = ["viewer", "noc", "admin"] as const;
  const rows: [Key, [Key, Key, Key]][] = [
    ["set.perm.monitoring", ["set.perm.view", "set.perm.view", "set.perm.edit"]],
    ["set.perm.integrations", ["set.perm.view", "set.perm.view", "set.perm.manage"]],
    ["set.perm.credentials", ["set.perm.status", "set.perm.status", "set.perm.replace"]],
    ["set.perm.sharing", ["set.perm.none", "set.perm.none", "set.perm.manage"]],
  ];
  return (
    <div className="grid gap-3 xl:grid-cols-2">
      <Panel id="language" title={t("set.gen.language")}>
        <p className="mb-3 text-[12.5px] text-slate-400">{t("set.gen.languageHint")}</p>
        <div role="radiogroup" className="flex w-fit rounded-sm border border-rule p-0.5">
          {(["en", "id"] as const).map((l) => (
            <button key={l} type="button" role="radio" aria-checked={lang === l} onClick={() => setLang(l)}
              className={cn("rounded-sm px-3 py-1 text-[12.5px]", lang === l ? "bg-signal/10 text-slate-100" : "text-slate-400 hover:text-slate-100")}>
              {t(l === "en" ? "lang.en" : "lang.id")}
            </button>
          ))}
        </div>
      </Panel>
      <Panel id="system" title={t("set.gen.system")}>
        <dl className="grid gap-3 sm:grid-cols-3">
          <Field label={t("set.gen.publicUrl")}>{g.public_base_url || <span className="text-signal-warn">{t("set.notSet")}</span>}</Field>
          <Field label={t("set.gen.tz")}>{g.olt_timezone || <span className="text-slate-400">{t("set.gen.tzUnset")}</span>}</Field>
          <Field label={t("set.gen.inviteTtl")}>{durText(g.invite_ttl_s * 1000)}</Field>
        </dl>
        <p className="mt-3 text-[11.5px] text-slate-500">{t("set.envManaged")}</p>
      </Panel>
      <Panel id="permissions" title={t("set.gen.permissions")} className="xl:col-span-2">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="border-b border-rule text-left">
              <th className="eyebrow py-2 pr-4 font-medium">{t("set.perm.area")}</th>
              {roles.map((r) => <th key={r} className="eyebrow py-2 pr-4 font-medium">{t(`ua.role.${r}` as Key)}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map(([area, cells]) => (
              <tr key={area} className="border-b border-rule/60 last:border-b-0">
                <td className="py-2 pr-4 text-slate-200">{t(area)}</td>
                {cells.map((c, i) => (
                  <td key={i} className={cn("py-2 pr-4", c === "set.perm.none" ? "text-slate-600" : c === "set.perm.view" || c === "set.perm.status" ? "text-slate-400" : "text-slate-100")}>{t(c)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-3 text-[11.5px] text-slate-500">{t("set.perm.note")}</p>
      </Panel>
    </div>
  );
}

// ---------- monitoring ----------

function Monitoring({ s, onDirty }: { s: Summary; onDirty: (d: boolean) => void }) {
  const { t, locale } = useI18n();
  const rules = useQuery({ queryKey: ["alert-rules"], queryFn: listAlertRules });
  const all = rules.data?.rules ?? [];
  const m = s.monitoring;
  const fresh: [Key, number][] = [
    ["set.mon.oltPoll", m.olt_poll_s], ["set.mon.snmpPoll", m.snmp_poll_s], ["set.mon.acsSync", m.sync_s],
    ["set.mon.whmcsSync", m.whmcs_sync_s], ["set.mon.alertEval", m.alert_eval_s],
  ];
  return (
    <div className="space-y-3">
      <OpticalPolicy s={s} onDirty={onDirty} />
      <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <Panel id="freshness" title={t("set.mon.freshness")}>
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="border-b border-rule text-left">
                <th className="eyebrow py-1.5 pr-3 font-medium">{t("set.mon.job")}</th>
                <th className="eyebrow py-1.5 pr-3 font-medium">{t("set.mon.interval")}</th>
                <th className="eyebrow py-1.5 font-medium">{t("set.mon.staleAfter")}</th>
              </tr>
            </thead>
            <tbody>
              {fresh.map(([k, sec]) => (
                <tr key={k} className="border-b border-rule/60 last:border-b-0">
                  <td className="py-1.5 pr-3 text-slate-200">{t(k)}</td>
                  <td className="data py-1.5 pr-3 text-slate-100">{sec > 0 ? t("set.every", { d: everyText(sec * 1000) }) : "—"}</td>
                  <td className="data py-1.5 text-slate-400">{sec > 0 ? everyText((2 * sec + 30) * 1000) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-[11.5px] text-slate-500">{t("set.mon.staleRule")}</p>
        </Panel>
        <div className="space-y-3">
          <Panel id="alert-policies" title={t("set.mon.alertPolicies")}
            right={<Link to="/alerts?view=rules" className="inline-flex items-center gap-1 text-[12px] text-slate-400 hover:text-signal">{t("set.mon.manageRules")} <FiArrowRight className="size-3" /></Link>}>
            <dl className="grid grid-cols-3 gap-3">
              <Field label={t("set.mon.rules")}>{rules.data ? fmtNum(locale, all.length) : "—"}</Field>
              <Field label={t("common.enabled")}>{rules.data ? fmtNum(locale, all.filter((r) => r.enabled).length) : "—"}</Field>
              <Field label={t("set.mon.critical")}>{rules.data ? fmtNum(locale, all.filter((r) => r.enabled && r.severity === "critical").length) : "—"}</Field>
            </dl>
          </Panel>
          <Panel title={t("set.mon.util")}>
            <dl className="grid grid-cols-2 gap-3">
              <Field label={t("set.mon.utilHigh")}>≥ {m.util_high_pct}%</Field>
              <Field label={t("set.mon.utilCrit")}>≥ {m.util_crit_pct}%</Field>
            </dl>
            <p className="mt-2 text-[11.5px] text-slate-500">{t("set.mon.utilHint")}</p>
          </Panel>
        </div>
      </div>
    </div>
  );
}

const LEVEL_TONE: Record<SignalLevel, Tone> = { good: "good", warn: "warn", crit: "crit", unknown: "muted" };

function OpticalPolicy({ s, onDirty }: { s: Summary; onDirty: (d: boolean) => void }) {
  const { t, locale } = useI18n();
  const { user } = useAuth();
  const admin = user?.role === "admin";
  const qc = useQueryClient();
  const now = useNow(30_000);
  const rules = useQuery({ queryKey: ["alert-rules"], queryFn: listAlertRules });
  // Same cache entry as the ONT list, so opening Settings after it costs nothing.
  const onts = useQuery({ queryKey: ["onts", ""], queryFn: () => listONTs({ q: "", limit: 5000 }), staleTime: 60_000 });
  const cur = bandsOf(rules.data?.rules);
  const warnRule = rxRuleFor(rules.data?.rules, "warn");
  const critRule = rxRuleFor(rules.data?.rules, "critical");
  const [draft, setDraft] = useState<{ warn: string; crit: string } | null>(null);
  const [saved, setSaved] = useState(false);
  const shown = draft ?? { warn: String(cur.warn), crit: String(cur.crit) };
  const err = policyError(shown.warn, shown.crit);
  const next: SignalBands | null = err ? null : { warn: Number(shown.warn), crit: Number(shown.crit) };
  const changed = !!draft && (!!err || next!.warn !== cur.warn || next!.crit !== cur.crit);
  useEffect(() => { onDirty(changed); }, [changed, onDirty]);
  useEffect(() => {
    if (!saved) return;
    const id = setTimeout(() => setSaved(false), 4000);
    return () => clearTimeout(id);
  }, [saved]);

  const save = useMutation({
    mutationFn: async (b: SignalBands) => {
      const targets = [
        { sev: "warn" as const, value: b.warn, name: "ONT Rx power warning", rule: warnRule },
        { sev: "critical" as const, value: b.crit, name: "ONT Rx power critical", rule: critRule },
      ];
      for (const x of targets) {
        if (x.rule) {
          if (x.rule.threshold !== x.value) await updateAlertRule(x.rule.id, { threshold: x.value });
          continue;
        }
        // A fresh install has only the critical rule: create the other with the same anti-flap knobs.
        await createAlertRule({
          name: x.name, metric: "ont.rx_power_dbm", condition: "<", threshold: x.value,
          severity: x.sev, hysteresis: 1, consecutive: 2, cooldown_s: 1800, enabled: true,
        });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["alert-rules"] });
      qc.invalidateQueries({ queryKey: ["settings-summary"] });
      setDraft(null);
      setSaved(true);
    },
  });

  const view = next ?? cur;
  const rx = (onts.data?.onts ?? []).map((o) => o.rx_power_dbm);
  const before = impact(rx, cur);
  const after = changed && next ? impact(rx, next) : null;
  const last = [warnRule, critRule].map((r) => (r ? s.rule_changes[String(r.id)] : undefined)).filter(Boolean)
    .sort((a, b) => Date.parse(b!.at) - Date.parse(a!.at))[0];

  const input = (key: "warn" | "crit", label: Key) => (
    <label className="block">
      <span className="text-[11.5px] text-slate-400">{t(label)}</span>
      <div className="mt-1 flex items-center gap-2">
        <Input type="number" step="0.5" inputMode="decimal" disabled={!admin} aria-invalid={!!err}
          className={cn("h-8 w-24 font-mono text-[13px]", err && "border-signal-crit/60")}
          value={shown[key]} onChange={(e) => { setSaved(false); setDraft({ ...shown, [key]: e.target.value }); }} />
        <span className="text-[12px] text-slate-500">dBm</span>
      </div>
    </label>
  );

  return (
    <Panel id="optical" title={t("set.opt.title")}
      right={last && <span className="text-[11.5px] text-slate-500">{t("set.lastChanged", { age: durText(now - Date.parse(last.at)), by: last.by || "—" })}</span>}>
      {!admin && <div className="mb-3"><ViewOnly what="set.opt.title" /></div>}
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)_minmax(0,0.9fr)]">
        <div>
          <p className="eyebrow mb-2">{t("set.opt.bands")}</p>
          <ul className="overflow-hidden rounded-sm border border-rule text-[12.5px]">
            {([
              ["good", "set.opt.healthy", `≥ ${view.warn} dBm`],
              ["warn", "set.opt.warning", `< ${view.warn} dBm`],
              ["crit", "set.opt.critical", `< ${view.crit} dBm`],
            ] as [Tone, Key, string][]).map(([tone, k, v]) => (
              <li key={k} className="flex items-center justify-between border-b border-rule/60 px-3 py-1.5 last:border-b-0">
                <span className={cn("flex items-center gap-2", TONE[tone].text)}><Dot tone={tone} /> {t(k)}</span>
                <span className="data text-slate-100">{v}</span>
              </li>
            ))}
          </ul>
          <p className="mt-1.5 text-[11px] text-slate-500">{t("set.opt.overload")}</p>
          <div className="mt-3 flex gap-5">
            {input("warn", "set.opt.warnField")}
            {input("crit", "set.opt.critField")}
          </div>
          {err && <p role="alert" className="mt-2 flex items-center gap-1.5 text-[12px] text-signal-crit"><FiAlertTriangle className="size-3.5" /> {t(`set.opt.err.${err}` as Key)}</p>}
        </div>

        <div>
          <p className="eyebrow mb-2">{t("set.opt.preview")}</p>
          <ul className="space-y-1 text-[12.5px]">
            {previewSamples(view).map((v) => {
              const lvl = classifyRx(v, view);
              return (
                <li key={v} className="flex items-center justify-between rounded-sm bg-white/[0.02] px-3 py-1">
                  <span className="data text-slate-100">{v.toFixed(1)} dBm</span>
                  <Badge tone={LEVEL_TONE[lvl]}>{t(`set.opt.lvl.${lvl}` as Key)}</Badge>
                </li>
              );
            })}
          </ul>
          <p className="eyebrow mt-4 mb-1.5">{t("set.opt.impact")}</p>
          {onts.isLoading ? <p className="text-[12px] text-slate-500">{t("common.loading")}</p> : onts.error ? <p className="text-[12px] text-slate-500">—</p> : (
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="text-left text-[11px] text-slate-500">
                  <th className="pb-1 font-normal" />
                  <th className="pb-1 text-right font-normal">{t("set.opt.now")}</th>
                  {after && <th className="pb-1 text-right font-normal">{t("set.opt.after")}</th>}
                </tr>
              </thead>
              <tbody>
                {(["good", "warn", "crit", "unknown"] as SignalLevel[]).map((l) => {
                  const d = after ? after[l] - before[l] : 0;
                  return (
                    <tr key={l}>
                      <td className={cn("py-0.5", TONE[LEVEL_TONE[l]].text)}>{t(`set.opt.lvl.${l}` as Key)}</td>
                      <td className="data py-0.5 text-right text-slate-300">{fmtNum(locale, before[l])}</td>
                      {after && (
                        <td className="data py-0.5 text-right text-slate-100">
                          {fmtNum(locale, after[l])}
                          {d !== 0 && <span className={cn("ml-1.5", d > 0 && l !== "good" ? "text-signal-warn" : "text-slate-500")}>{d > 0 ? "+" : "−"}{fmtNum(locale, Math.abs(d))}</span>}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {rx.length >= 5000 && <p className="mt-1 text-[11px] text-slate-500">{t("set.opt.capped")}</p>}
        </div>

        <div className="space-y-3 text-[12.5px]">
          <div>
            <p className="eyebrow mb-1">{t("set.opt.appliesTo")}</p>
            <p className="text-slate-100">{t("set.opt.allOnts")}</p>
            <p className="mt-0.5 text-[11.5px] text-slate-500">{t("set.opt.overrides")}</p>
          </div>
          <div>
            <p className="eyebrow mb-1">{t("set.opt.usedBy")}</p>
            <ul className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-slate-300">
              {(["set.use.ontList", "set.use.ontDetail", "set.use.oltDetail", "set.use.wallboard", "set.use.alerts", "set.use.telegram"] as Key[]).map((k) => <li key={k}>{t(k)}</li>)}
            </ul>
          </div>
        </div>
      </div>

      {admin && (changed || saved || save.error) && (
        <div className={cn("mt-4 flex flex-wrap items-center justify-end gap-3 border-t border-rule pt-3", changed && "border-amber-400/30")}>
          {changed && <span className="mr-auto flex items-center gap-2 text-[12.5px] text-signal-warn"><Dot tone="warn" /> {t("set.unsaved")}</span>}
          {!changed && saved && <span className="mr-auto flex items-center gap-2 text-[12.5px] text-signal-good"><FiCheckCircle className="size-3.5" /> {t("set.saved")}</span>}
          {save.error && <span className="text-[12px] text-signal-crit">{(save.error as Error).message}</span>}
          {changed && <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>{t("set.discard")}</Button>}
          {changed && <Button size="sm" disabled={!!err || save.isPending} onClick={() => next && save.mutate(next)}>{t(save.isPending ? "common.saving" : "set.saveChanges")}</Button>}
        </div>
      )}
    </Panel>
  );
}

// ---------- integrations ----------

const ACS_UI: Record<AcsState, { tone: Tone; icon: IconType }> = {
  healthy: { tone: "good", icon: FiCheckCircle },
  degraded: { tone: "warn", icon: FiAlertTriangle },
  failed: { tone: "crit", icon: FiXCircle },
  disabled: { tone: "muted", icon: FiMinusCircle },
  unknown: { tone: "muted", icon: FiHelpCircle },
};

function Integrations({ s }: { s: Summary }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const admin = user?.role === "admin";
  const i = s.integrations;
  const rows: { name: string; purpose: Key; ok: boolean; detail: string }[] = [
    { name: "LibreNMS", purpose: "set.int.librenms", ok: i.librenms.configured, detail: i.librenms.host || "—" },
    {
      name: "SMTP", purpose: "set.int.smtp", ok: i.smtp.configured,
      detail: i.smtp.configured ? `${i.smtp.host} · ${t(i.smtp.auth ? "set.int.auth" : "set.int.noAuth")} · ${t("set.int.recipients", { n: i.smtp.recipients })}` : "—",
    },
    {
      name: "Telegram", purpose: "set.int.telegram", ok: i.telegram.configured,
      detail: i.telegram.configured ? t("set.int.botChat") : [!i.telegram.token && t("set.int.noToken"), !i.telegram.chat && t("set.int.noChat")].filter(Boolean).join(" · "),
    },
    { name: "WHMCS", purpose: "set.int.whmcs", ok: i.whmcs.configured, detail: i.whmcs.host || "—" },
  ];
  return (
    <div className="space-y-3">
      {!admin && <ViewOnly what="set.nav.integrations" />}
      <AcsServers />
      <Panel id="connections" title={t("set.int.connections")}
        right={<Link to="/integrations" className="inline-flex items-center gap-1 text-[12px] text-slate-400 hover:text-signal">{t("set.int.liveHealth")} <FiArrowRight className="size-3" /></Link>}>
        <table className="w-full text-[12.5px]">
          <tbody>
            {rows.map((r) => (
              <tr key={r.name} className="border-b border-rule/60 last:border-b-0">
                <td className="w-36 py-2 pr-3 font-medium text-slate-100">{r.name}</td>
                <td className="py-2 pr-3 text-slate-400">{t(r.purpose)}</td>
                <td className="w-40 py-2 pr-3">{r.ok ? <Badge tone="good">{t("set.configured")}</Badge> : <Badge tone="muted">{t("set.notConfigured")}</Badge>}</td>
                <td className="data py-2 text-right text-slate-400">{r.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-2 text-[11.5px] text-slate-500">{t("set.envManaged")}</p>
      </Panel>
    </div>
  );
}

type Note = { busy?: boolean; ok?: string; err?: string };

function AcsServers() {
  const { t, locale } = useI18n();
  const { user } = useAuth();
  const admin = user?.role === "admin";
  const qc = useQueryClient();
  const now = useNow(15_000);
  const { data, error, isLoading } = useQuery({ queryKey: ["acs-servers"], queryFn: listACSServers, refetchInterval: 60_000 });
  const ints = useIntegrations();
  const [dialog, setDialog] = useState<{ kind: "add" | "edit" | "delete" | "toggle"; s?: ACSServer } | null>(null);
  const [notes, setNotes] = useState<Record<number, Note>>({});
  const note = (id: number, n: Note) => setNotes((m) => ({ ...m, [id]: n }));
  const run = useMutation({
    mutationFn: ({ id, kind }: { id: number; kind: "test" | "sync" }) => (kind === "test" ? testACSServer(id) : syncACSServer(id)),
    onMutate: ({ id }) => note(id, { busy: true }),
    onSuccess: (d, { id, kind }) => note(id, { ok: t(kind === "test" ? "settings.connected" : "settings.syncedDevices", { n: fmtNum(locale, d.devices) }) }),
    onError: (e: Error, { id }) => note(id, { err: e.message }),
    onSettled: () => { qc.invalidateQueries({ queryKey: ["acs-servers"] }); qc.invalidateQueries({ queryKey: ["integrations"] }); },
  });
  const toggle = useMutation({
    mutationFn: (x: ACSServer) => updateACSServer(x.id, { enabled: !x.enabled }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["acs-servers"] }); setDialog(null); },
  });
  const list = data?.acs_servers ?? [];

  return (
    <Panel id="acs" title={`${t("set.acs.title")} · ${fmtNum(locale, list.length)}`}
      right={admin && <Button size="sm" onClick={() => setDialog({ kind: "add" })}><FiPlus /> {t("settings.addServer")}</Button>}>
      {isLoading && <p className="text-slate-400">{t("common.loading")}</p>}
      {error && <p className="text-signal-crit">{(error as Error).message}</p>}
      {data && list.length === 0 && <p className="text-[12.5px] text-slate-500">{t(admin ? "settings.noServersAdmin" : "settings.noServers")}</p>}
      <div className="grid gap-3 lg:grid-cols-2 2xl:grid-cols-3">
        {list.map((x) => {
          const r = ints.data?.integrations.find((i) => i.name === `genieacs:${x.name}`);
          const { state, devices } = acsState(x, r, now);
          const ui = ACS_UI[state];
          const n = notes[x.id];
          const problem = state !== "healthy" && state !== "disabled" ? (x.last_error || (r?.last_run.status === "error" ? r.last_run.detail : null)) : null;
          return (
            <article key={x.id} className={cn("flex flex-col rounded-md border border-rule bg-white/[0.015] p-3", state === "disabled" && "opacity-70")}>
              <header className="flex items-start justify-between gap-2">
                <h3 className="truncate text-[13.5px] font-semibold tracking-wide text-slate-100 uppercase">{x.name}</h3>
                <Badge tone={ui.tone} icon={ui.icon}>{t(`set.acs.state.${state}` as Key)}</Badge>
              </header>
              <dl className="mt-2.5 grid grid-cols-2 gap-x-4 gap-y-2">
                <Field label={t("set.acs.endpoint")}><span title={x.base_url}>{endpointOf(x.base_url)}</span></Field>
                <Field label={t("set.acs.devices")}>
                  {devices.kind === "never" ? <span className="text-slate-400">{t("set.acs.notSynced")}</span>
                    : <>{fmtNum(locale, devices.n ?? 0)}{devices.kind === "stale" && <span className="ml-1.5 text-signal-warn">{t("set.acs.stale")}</span>}</>}
                </Field>
                <Field label={t("settings.lastChecked")}>{x.last_checked_at ? <span title={fmtDateTime(locale, x.last_checked_at)}>{ago(x.last_checked_at, now, t)}</span> : <span className="text-slate-400">{t("set.acs.neverTested")}</span>}</Field>
                <Field label={t("set.acs.lastSync")}>{r?.last_ok_at ? <span title={fmtDateTime(locale, r.last_ok_at)}>{ago(r.last_ok_at, now, t)}</span> : <span className="text-slate-400">{t("common.never")}</span>}</Field>
              </dl>
              {problem && <p className="mt-2 line-clamp-2 text-[11.5px] text-signal-crit/90" title={problem}>{problem}</p>}
              {n && (
                <p className={cn("mt-2 text-[11.5px]", n.err ? "text-signal-crit" : n.ok ? "text-signal-good" : "text-slate-400")}>
                  {n.busy ? t("settings.working") : n.err ?? n.ok}
                </p>
              )}
              <span className="min-h-3 flex-1" />
              {admin && (
                <footer className="mt-auto flex items-center gap-2 border-t border-rule/60 pt-2.5">
                  <Button size="xs" variant="outline" disabled={n?.busy} onClick={() => run.mutate({ id: x.id, kind: "test" })}>{t("set.acs.test")}</Button>
                  <Button size="xs" variant="outline" disabled={n?.busy || !x.enabled} onClick={() => run.mutate({ id: x.id, kind: "sync" })}><FiRefreshCw /> {t("set.acs.sync")}</Button>
                  <span className="flex-1" />
                  <Menu label={t("set.actionsFor", { name: x.name })} items={[
                    { icon: FiEdit2, label: t("common.edit"), onClick: () => setDialog({ kind: "edit", s: x }) },
                    { icon: FiPower, label: t(x.enabled ? "set.acs.disable" : "set.acs.enable"), onClick: () => (x.enabled ? setDialog({ kind: "toggle", s: x }) : toggle.mutate(x)) },
                    "sep",
                    { icon: FiTrash2, label: t("common.delete"), danger: true, onClick: () => setDialog({ kind: "delete", s: x }) },
                  ]} />
                </footer>
              )}
            </article>
          );
        })}
      </div>
      <p className="mt-3 flex items-center gap-2 text-[11.5px] text-slate-500"><FiLock className="size-3" /> {t("settings.passwordNote")}</p>
      {dialog?.kind === "add" && <ACSDialog edit={null} onClose={() => setDialog(null)} />}
      {dialog?.kind === "edit" && dialog.s && <ACSDialog edit={dialog.s} onClose={() => setDialog(null)} />}
      {dialog?.kind === "delete" && dialog.s && <DeleteDialog server={dialog.s} onClose={() => setDialog(null)} />}
      {dialog?.kind === "toggle" && dialog.s && (
        <Confirm title={t("set.acs.disableTitle", { name: dialog.s.name })} body={t("set.acs.disableBody")} action={t("set.acs.disable")}
          pending={toggle.isPending} error={toggle.error} onConfirm={() => toggle.mutate(dialog.s!)} onClose={() => setDialog(null)} />
      )}
    </Panel>
  );
}

const emptyAcs: ACSServerForm = { name: "", base_url: "http://:7557", username: "", password: "", enabled: true };

function ACSDialog({ edit, onClose }: { edit: ACSServer | null; onClose: () => void }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [form, setForm] = useState<ACSServerForm>(
    edit ? { name: edit.name, base_url: edit.base_url, username: edit.username ?? "", password: "", enabled: edit.enabled, notes: edit.notes ?? "" } : emptyAcs,
  );
  const save = useMutation({
    // Blank password means "leave the stored one alone", so never send it.
    mutationFn: (body: ACSServerForm) => {
      const payload = { ...body, password: body.password || undefined };
      return edit ? updateACSServer(edit.id, payload) : createACSServer(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["acs-servers"] });
      qc.invalidateQueries({ queryKey: ["settings-summary"] });
      onClose();
    },
  });
  const field = (key: keyof ACSServerForm, label: string, type = "text", hint?: string) => (
    <label className="block text-xs text-slate-400">
      {label}
      <Input type={type} autoComplete={type === "password" ? "new-password" : undefined} value={String(form[key] ?? "")} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
      {hint && <span className="mt-1 block text-[11px] text-slate-500">{hint}</span>}
    </label>
  );
  const urlOK = /^https?:\/\/\S+$/.test(form.base_url.trim());
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{edit ? t("settings.editTitle", { name: edit.name }) : t("settings.addServerTitle")}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          {field("name", t("common.name"))}
          {field("base_url", t("settings.nbiUrl"), "text", t("settings.nbiHint"))}
          {field("username", t("settings.username"))}
          {field("password", t(edit ? "settings.passwordKeep" : "settings.password"), "password", t(edit ? "settings.passwordKeepHint" : "settings.passwordHint"))}
          {field("notes", t("settings.notesOptional"))}
          <label className="flex items-center gap-2 text-xs text-slate-400">
            <input type="checkbox" checked={form.enabled !== false} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
            {t("settings.enabledHint")}
          </label>
        </div>
        {!urlOK && form.base_url.trim() !== "" && <p className="text-amber-300">{t("settings.urlScheme")}</p>}
        {save.error && <p className="text-signal-crit">{(save.error as Error).message}</p>}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
          <Button disabled={save.isPending || !form.name.trim() || !urlOK} onClick={() => save.mutate(form)}>
            {t(save.isPending ? "common.saving" : "common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({ server, onClose }: { server: ACSServer; onClose: () => void }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const del = useMutation({
    mutationFn: () => deleteACSServer(server.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["acs-servers"] });
      qc.invalidateQueries({ queryKey: ["integrations"] });
      qc.invalidateQueries({ queryKey: ["settings-summary"] });
      onClose();
    },
  });
  return (
    <Confirm title={t("settings.deleteTitle", { name: server.name })} body={t("settings.deleteBody", { n: server.device_count })}
      action={t(del.isPending ? "alerts.deleting" : "set.acs.deleteServer")} danger pending={del.isPending} error={del.error}
      onConfirm={() => del.mutate()} onClose={onClose} />
  );
}

// ---------- access & credentials ----------

function Access({ s, go }: { s: Summary; go: (s: Section, anchor?: string) => void }) {
  const { t, locale } = useI18n();
  const { user } = useAuth();
  const admin = user?.role === "admin";
  const now = useNow(30_000);
  const sec = s.security;
  const [replace, setReplace] = useState<Summary["olts"][number] | null>(null);
  const missing = sec.olts - sec.olt_credentials;
  const posture: [Key, ReactNode][] = [
    ["set.sec.encryption", sec.vault_configured ? <Badge tone="good" icon={FiShield}>{t("set.enc.enabled")}</Badge> : <Badge tone="crit" icon={FiAlertTriangle}>{t("set.enc.notConfigured")}</Badge>],
    ["set.sec.masterKey", sec.vault_configured ? <span className="text-signal-good">{t("set.configured")}</span> : <span className="text-signal-crit">{t("set.missing")}</span>],
    ["set.sec.algorithm", <span className="data">{sec.algorithm}</span>],
    ["set.sec.oltStored", <span className={missing > 0 ? "text-signal-warn" : ""}>{t("set.nOfM", { n: sec.olt_credentials, m: sec.olts })}</span>],
    ["set.sec.acsStored", <span className="data">{fmtNum(locale, sec.acs_credentials)}</span>],
    ["set.sec.plaintext", <span className="text-signal-good">{t("set.sec.plaintextNone")}</span>],
    ["set.sec.reveal", <span className="text-slate-300">{t("set.sec.revealOff")}</span>],
  ];
  const login = (o: Summary["olts"][number]): { tone: Tone; text: string; title?: string } =>
    o.last_error ? { tone: "crit", text: t("set.cred.pollFailing"), title: o.last_error }
      : o.last_polled_at ? { tone: "good", text: t("set.cred.accepted", { age: durText(now - Date.parse(o.last_polled_at)) }) }
      : { tone: "muted", text: t("set.cred.notVerified") };

  return (
    <div className="space-y-3">
      {!admin && <ViewOnly what="set.nav.access" />}
      <div className="grid gap-3 2xl:grid-cols-[380px_minmax(0,1fr)]">
        <Panel id="encryption" title={t("set.sec.title")}>
          <dl className="space-y-2">
            {posture.map(([k, v]) => (
              <div key={k} className="flex items-center justify-between gap-3 text-[12.5px]">
                <dt className="text-slate-400">{t(k)}</dt><dd className="text-right text-slate-100">{v}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-3 text-[12px] text-slate-400">
            {t("set.sec.summary")}{" "}
            <button type="button" onClick={() => go("advanced", "crypto")} className="text-signal hover:underline">{t("set.learnMore")}</button>
          </p>
        </Panel>

        <Panel id="olt-credentials" title={t("set.cred.title")}>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-[12.5px]">
              <thead>
                <tr className="border-b border-rule text-left">
                  {(["set.cred.olt", "set.cred.username", "set.cred.access", "set.cred.secrets", "set.cred.changed", "set.cred.login"] as Key[]).map((k) => (
                    <th key={k} className="eyebrow py-2 pr-3 font-medium">{t(k)}</th>
                  ))}
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody>
                {s.olts.map((o) => {
                  const l = login(o);
                  return (
                    <tr key={o.id} className="border-b border-rule/60 last:border-b-0">
                      <td className="py-2 pr-3"><Link to={`/olts/${o.id}`} className="font-medium text-slate-100 hover:text-signal">{o.name}</Link></td>
                      <td className="data py-2 pr-3 text-slate-300">{o.username ?? <span className="text-slate-500">{t("set.cred.hidden")}</span>}</td>
                      <td className="data py-2 pr-3 text-slate-300">{o.protocol.toUpperCase()} :{o.port}</td>
                      <td className="py-2 pr-3">
                        <span className={cn("inline-flex items-center gap-1.5", o.has_password ? "text-slate-200" : "text-signal-crit")}>
                          <FiLock className="size-3" /> {t(o.has_password ? "set.cred.stored" : "set.cred.noPassword")}
                        </span>
                        {o.has_enable_password && <span className="ml-2 text-[11px] text-slate-500">{t("set.cred.plusEnable")}</span>}
                      </td>
                      <td className="py-2 pr-3 text-slate-400">
                        {o.changed ? <span title={fmtDateTime(locale, o.changed.at)}>{fmtDate(locale, o.changed.at)}<span className="block text-[11px] text-slate-500">{o.changed.by || "—"}</span></span>
                          : <span className="text-slate-500">{t("set.cred.beforeAudit")}</span>}
                      </td>
                      <td className={cn("py-2 pr-3", TONE[l.tone].text)} title={l.title}><span className="inline-flex items-center gap-1.5"><Dot tone={l.tone} /> {l.text}</span></td>
                      <td className="text-right">
                        {admin && <Menu label={t("set.actionsFor", { name: o.name })} items={[{ icon: FiKey, label: t("set.cred.replace"), onClick: () => setReplace(o) }]} />}
                      </td>
                    </tr>
                  );
                })}
                {s.olts.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-slate-500">{t("set.cred.none")}</td></tr>}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-[11.5px] text-slate-500">{t("set.cred.writeOnly")}</p>
        </Panel>
      </div>
      {replace && <ReplaceCredential olt={replace} onClose={() => setReplace(null)} />}
    </div>
  );
}

function ReplaceCredential({ olt, onClose }: { olt: Summary["olts"][number]; onClose: () => void }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [user, setUser] = useState(olt.username ?? "");
  const [pass, setPass] = useState("");
  const [enable, setEnable] = useState("");
  const save = useMutation({
    // Blank enable password keeps the stored one; the server never sends either back.
    mutationFn: () => updateOLT(olt.id, { mgmt_username: user.trim(), mgmt_password: pass, enable_password: enable || undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["settings-summary"] });
      qc.invalidateQueries({ queryKey: ["olts"] });
      onClose();
    },
  });
  return (
    <Confirm title={t("set.cred.replaceTitle", { name: olt.name })} body={t("set.cred.replaceBody")} action={t(save.isPending ? "common.saving" : "set.cred.replace")}
      danger pending={save.isPending || !user.trim() || !pass} error={save.error} onConfirm={() => save.mutate()} onClose={onClose}>
      <div className="grid gap-3">
        <label className="block text-xs text-slate-400">{t("olts.cliUser")}<Input value={user} onChange={(e) => setUser(e.target.value)} autoComplete="off" /></label>
        <label className="block text-xs text-slate-400">{t("set.cred.newPassword")}<Input type="password" autoComplete="new-password" value={pass} onChange={(e) => setPass(e.target.value)} /></label>
        <label className="block text-xs text-slate-400">{t("set.cred.newEnable")}<Input type="password" autoComplete="new-password" value={enable} onChange={(e) => setEnable(e.target.value)} /></label>
      </div>
    </Confirm>
  );
}

// ---------- sharing ----------

type ShareFilter = "active" | "expired" | "revoked" | "all";
const SHARE_TONE: Record<GraphShare["status"], Tone> = { active: "good", expired: "warn", revoked: "muted" };

function Sharing() {
  const { t, locale } = useI18n();
  const { user } = useAuth();
  const admin = user?.role === "admin";
  const now = useNow(60_000);
  const { data, error, isLoading } = useQuery({ queryKey: ["graph-shares"], queryFn: listGraphShares, enabled: admin });
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState<ShareFilter>("active");
  const [dlg, setDlg] = useState<{ kind: "extend" | "revoke"; s: GraphShare } | null>(null);
  if (!admin) return <Panel id="shares" title={t("shares.title")}><ViewOnly what="shares.title" /></Panel>;
  const all = data?.shares ?? [];
  const count = (f: ShareFilter) => (f === "all" ? all.length : all.filter((x) => x.status === f).length);
  const shown = all.filter((x) => filter === "all" || x.status === filter);

  return (
    <div className="space-y-3">
      <Panel id="shares" title={`${t("shares.title")} · ${t("set.nActive", { n: count("active") })}`}
        right={<Button size="sm" onClick={() => setCreating(true)}><FiPlus /> {t("set.share.create")}</Button>}>
        <p className="mb-3 flex items-start gap-2 rounded-sm border border-amber-400/20 bg-amber-400/[0.04] px-3 py-2 text-[12px] text-slate-300">
          <FiShield className="mt-0.5 size-3.5 shrink-0 text-signal-warn" /> {t("set.share.policy")}
        </p>
        <div className="mb-3 flex flex-wrap gap-2">
          {(["active", "expired", "revoked", "all"] as ShareFilter[]).map((f) => (
            <button key={f} type="button" aria-pressed={filter === f} onClick={() => setFilter(f)}
              className={cn("rounded-sm border px-2 py-1 text-[12px] capitalize", filter === f ? "border-signal/50 bg-signal/10 text-slate-100" : "border-rule text-slate-400 hover:text-slate-100")}>
              {t(f === "all" ? "common.all" : (`shares.${f}` as Key))} <span className="data text-slate-500">{fmtNum(locale, count(f))}</span>
            </button>
          ))}
        </div>
        {isLoading && <p className="text-slate-400">{t("common.loading")}</p>}
        {error && <p className="text-signal-crit">{(error as Error).message}</p>}
        {data && shown.length === 0 && <p className="py-4 text-center text-[12.5px] text-slate-500">{t("shares.empty")}</p>}
        <div className="grid gap-3 lg:grid-cols-2 2xl:grid-cols-3">
          {shown.map((x) => {
            const left = x.expires_at ? Date.parse(x.expires_at) - now : null;
            const soon = x.status === "active" && left !== null && left < 7 * 86_400_000;
            return (
              <article key={x.id} className={cn("flex flex-col rounded-md border border-rule bg-white/[0.015] p-3", x.status !== "active" && "opacity-70")}>
                <header className="flex items-start justify-between gap-2">
                  <h3 className="truncate text-[13.5px] font-semibold tracking-wide text-slate-100 uppercase">{x.label}</h3>
                  <Badge tone={SHARE_TONE[x.status]}>{t(`shares.${x.status}` as Key)}</Badge>
                </header>
                <div className="mt-2">
                  <p className="text-[11px] text-slate-500">{t("set.share.shared", { n: x.ports.length })}</p>
                  <ul className="data mt-0.5 text-[12.5px] text-slate-200">
                    {x.ports.slice(0, 4).map((p) => <li key={p} className="truncate">{p}</li>)}
                    {x.ports.length > 4 && <li className="text-slate-500">{t("set.share.more", { n: x.ports.length - 4 })}</li>}
                  </ul>
                </div>
                <dl className="mt-2.5 grid grid-cols-2 gap-x-4 gap-y-2">
                  <Field label={t("shares.views")}>{fmtNum(locale, x.view_count)}</Field>
                  <Field label={t("set.share.lastAccess")}>{x.last_viewed_at ? <span title={fmtDateTime(locale, x.last_viewed_at)}>{ago(x.last_viewed_at, now, t)}</span> : <span className="text-slate-400">{t("set.share.neverOpened")}</span>}</Field>
                  <Field label={t("common.created")}><span title={x.created_by_email ?? undefined}>{fmtDate(locale, x.created_at)}</span></Field>
                  <Field label={t("shares.expiry")}>
                    {x.status === "revoked" ? <span className="text-slate-400">{t("shares.revoked")}</span>
                      : x.expires_at ? <span className={soon ? "text-signal-warn" : ""} title={fmtDateTime(locale, x.expires_at)}>{fmtDate(locale, x.expires_at)}</span>
                      : <span className="text-signal-warn">{t("shares.never")}</span>}
                  </Field>
                </dl>
                <span className="min-h-3 flex-1" />
                <footer className="mt-auto flex items-center gap-2 border-t border-rule/60 pt-2.5 text-[11px] text-slate-500">
                  <FiLock className="size-3 shrink-0" />
                  <span className="flex-1 truncate">{x.created_by_email ? t("set.share.by", { email: x.created_by_email }) : t("set.share.onceShort")}</span>
                  {x.status !== "revoked" && (
                    <Menu label={t("set.actionsFor", { name: x.label })} items={[
                      { icon: FiCalendar, label: t("set.share.extend"), onClick: () => setDlg({ kind: "extend", s: x }) },
                      "sep",
                      { icon: FiXCircle, label: t("shares.revoke"), danger: true, onClick: () => setDlg({ kind: "revoke", s: x }) },
                    ]} />
                  )}
                </footer>
              </article>
            );
          })}
        </div>
      </Panel>
      {creating && <ShareDialog onClose={() => setCreating(false)} />}
      {dlg?.kind === "revoke" && <RevokeShare s={dlg.s} onClose={() => setDlg(null)} />}
      {dlg?.kind === "extend" && <ExtendShare s={dlg.s} onClose={() => setDlg(null)} />}
    </div>
  );
}

function RevokeShare({ s, onClose }: { s: GraphShare; onClose: () => void }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => revokeGraphShare(s.id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["graph-shares"] }); qc.invalidateQueries({ queryKey: ["settings-summary"] }); onClose(); },
  });
  return (
    <Confirm title={t("set.share.revokeTitle")} body={t("set.share.revokeBody", { name: s.label })} action={t("set.share.revokeLink")}
      danger pending={m.isPending} error={m.error} onConfirm={() => m.mutate()} onClose={onClose} />
  );
}

function ExtendShare({ s, onClose }: { s: GraphShare; onClose: () => void }) {
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const [days, setDays] = useState(90);
  const m = useMutation({
    mutationFn: () => extendGraphShare(s.id, days),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["graph-shares"] }); qc.invalidateQueries({ queryKey: ["settings-summary"] }); onClose(); },
  });
  const until = new Date(Date.now() + days * 86_400_000).toISOString();
  return (
    <Confirm title={t("set.share.extendTitle", { name: s.label })} body={t(s.status === "expired" ? "set.share.extendExpired" : "set.share.extendBody")}
      action={t("set.share.extend")} pending={m.isPending} error={m.error} onConfirm={() => m.mutate()} onClose={onClose}>
      <div className="flex flex-wrap gap-2">
        {[30, 90, 365].map((d) => (
          <button key={d} type="button" aria-pressed={days === d} onClick={() => setDays(d)}
            className={cn("rounded-sm border px-2.5 py-1 text-[12.5px]", days === d ? "border-signal/50 bg-signal/10 text-slate-100" : "border-rule text-slate-400 hover:text-slate-100")}>
            {t("shares.expiryDays", { n: d })}
          </button>
        ))}
      </div>
      <p className="text-[12.5px] text-slate-400">{t("set.share.newExpiry")} <span className="data text-slate-100">{fmtDate(locale, until)}</span></p>
    </Confirm>
  );
}

// ---------- advanced ----------

function Advanced({ s }: { s: Summary }) {
  const { t } = useI18n();
  const i = s.integrations;
  const rows: [Key, string, boolean | null][] = [
    ["set.sec.masterKey", "OPS_MASTER_KEY", s.security.vault_configured],
    ["set.adv.librenms", "LIBRENMS_URL, LIBRENMS_API_TOKEN", i.librenms.configured],
    ["set.adv.smtp", "SMTP_HOST, SMTP_PORT, SMTP_USER, SMTP_PASSWORD, SMTP_FROM, ALERT_EMAIL_TO", i.smtp.configured],
    ["set.adv.telegram", "TELEGRAM_BOT_TOKEN, TELEGRAM_OPS_CHAT_ID", i.telegram.configured],
    ["set.adv.whmcs", "WHMCS_URL, WHMCS_API_IDENTIFIER, WHMCS_API_SECRET, WHMCS_ACCESS_KEY", i.whmcs.configured],
    ["set.gen.publicUrl", "PUBLIC_BASE_URL (WEB_ORIGIN)", !!s.general.public_base_url],
    ["set.gen.tz", "OLT_TIMEZONE", !!s.general.olt_timezone],
    ["set.adv.intervals", "OLT_POLL_INTERVAL, SNMP_POLL_INTERVAL, INTEGRATION_SYNC_INTERVAL, WHMCS_SYNC_INTERVAL, ALERT_EVALUATION_INTERVAL", null],
    ["set.mon.util", "TRAFFIC_UTIL_HIGH_PCT, TRAFFIC_UTIL_CRIT_PCT", null],
    ["set.gen.inviteTtl", "USER_INVITE_TTL", null],
  ];
  return (
    <div className="space-y-3">
      <Panel id="diagnostics" title={t("set.adv.title")}>
        <p className="mb-3 text-[12px] text-slate-400">{t("set.adv.hint")}</p>
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="border-b border-rule text-left">
              <th className="eyebrow py-2 pr-3 font-medium">{t("set.adv.setting")}</th>
              <th className="eyebrow py-2 pr-3 font-medium">{t("set.adv.env")}</th>
              <th className="eyebrow py-2 font-medium">{t("common.status")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([k, env, ok]) => (
              <tr key={env} className="border-b border-rule/60 last:border-b-0">
                <td className="py-2 pr-3 text-slate-200">{t(k)}</td>
                <td className="py-2 pr-3 font-mono text-[11.5px] text-slate-400">{env}</td>
                <td className="py-2">{ok === null ? <span className="text-slate-500">{t("set.adv.defaults")}</span> : ok ? <Badge tone="good">{t("set.configured")}</Badge> : <Badge tone="muted">{t("set.notSet")}</Badge>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
      <Panel id="crypto" title={t("set.adv.crypto")}>
        <p className="max-w-3xl text-[12.5px] text-slate-400">{t("settings.oltCredentialsBody")}</p>
        <p className="mt-2 flex items-center gap-2 text-[11.5px] text-slate-500"><FiClock className="size-3" /> {t("set.adv.restart")}</p>
      </Panel>
    </div>
  );
}
